import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import MetricCard from "../../components/MetricCard.jsx";
import PlusNudge from "../../components/PlusNudge.jsx";
import { useI18n } from "../../i18n.jsx";
import { passagesJsonPath } from "../../lib/legacyPaths.js";
import { compareWritingToSource, countWords, getWords } from "../../lib/textMetrics.js";

const COPYING_LIMIT = 0.1;
const COPYING_NGRAM_SIZE = 3;

function adviceBarPercent(value, maximum) {
  const max = Math.max(Number(maximum) || 0, Number(value) || 0, 0.0001);
  return Math.max(0, Math.min(100, Math.round(((Number(value) || 0) / max) * 100)));
}

function SummaryAdviceModal({ advice, type, onSelect, onClose }) {
  const { t, locale } = useI18n();
  const label = (en, ja) => locale === "ja" ? ja : en;
  const closeRef = useRef(null);
  const bodyRef = useRef(null);
  const dialogRef = useRef(null);
  const closeAdvice = useRef(onClose);
  closeAdvice.current = onClose;
  const open = Boolean(advice);

  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeAdvice.current();
      if (event.key === "Tab") {
        const buttons = dialogRef.current.querySelectorAll("button");
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [open]);

  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0; }, [type]);
  if (!advice) return null;

  return createPortal(
    <div className="writing-advice-backdrop summary-advice-backdrop" role="presentation" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section ref={dialogRef} className="writing-advice-dialog summary-feedback" role="dialog" aria-modal="true" aria-labelledby="summary-advice-title" aria-describedby="summary-advice-copy">
        <header className="summary-feedback-header">
          <p className="summary-feedback-kicker">{label("SUMMARY FEEDBACK", "要約のフィードバック")}</p>
          <button ref={closeRef} className="writing-advice-close" type="button" onClick={onClose} aria-label={t("common.close")}>×</button>
          <h2 id="summary-advice-title">{advice.title}</h2>
          <div className="summary-feedback-tabs" role="group" aria-label={label("Feedback checks", "チェック項目")}>
            <button type="button" aria-pressed={type === "length"} onClick={() => onSelect("length")}>{t("writing.length")}</button>
            <button type="button" aria-pressed={type === "copy"} onClick={() => onSelect("copy")}>{t("summarizing.copying")}</button>
          </div>
        </header>
        <div ref={bodyRef} className="summary-feedback-body">
          <p className={`summary-feedback-status${advice.empty ? " is-empty" : advice.okay ? " is-good" : ""}`} role="status">
            {advice.empty ? label("Write a summary first", "まず要約を書きましょう") : advice.okay ? label("Goal met", "目標達成") : label("Needs revision", "修正しましょう")}
          </p>
          <p id="summary-advice-copy" className="summary-feedback-copy">{advice.message}</p>
          <div className="summary-feedback-chart" aria-label={advice.chartTitle}>
            {advice.bars.map((bar) => (
              <div className="summary-feedback-row" key={bar.label}>
                <span>{bar.label}</span>
                <div className="summary-feedback-track"><span className={bar.kind} style={{ width: `${adviceBarPercent(bar.value, advice.maxValue)}%` }} /></div>
                <strong>{bar.display}</strong>
              </div>
            ))}
          </div>
          {advice.feedback ? <div className="writing-advice-feedback">{advice.feedback}</div> : null}
        </div>
        <footer className="summary-feedback-footer">
          <button className="summary-feedback-return" type="button" onClick={onClose}>{label("Return to writing", "要約の編集に戻る")}</button>
        </footer>
      </section>
    </div>,
    document.body
  );
}

export default function SummarizingPage({ level }) {
  const { t, locale } = useI18n();
  const [passages, setPassages] = useState([]);
  const [passageIndex, setPassageIndex] = useState(0);
  const [text, setText] = useState("");
  const [adviceType, setAdviceType] = useState(null);

  useEffect(() => {
    fetch(`${passagesJsonPath(level.id)}?v=20261028`)
      .then((response) => response.json())
      .then(setPassages)
      .catch(() => setPassages([]));
  }, [level.id]);

  const passage = passages[passageIndex];
  const wordCount = useMemo(() => countWords(text), [text]);
  const copying = useMemo(
    () => compareWritingToSource(COPYING_NGRAM_SIZE, text, passage?.passage || ""),
    [passage?.passage, text]
  );
  const copyingPercent = copying.copyPCT;
  const copyingOkay = wordCount > 0 && copyingPercent <= COPYING_LIMIT;
  const inRange = passage ? wordCount >= passage.wordMin && wordCount <= passage.wordMax : false;
  const progress = passage ? Math.min(100, Math.round((wordCount / passage.wordMax) * 100)) : 0;
  const rangeDetail = passage ? t("summarizing.rangeWords", { min: passage.wordMin, max: passage.wordMax }) : t("summarizing.loading");
  const rangeStatus = !passage
    ? t("summarizing.loadingPassage")
    : wordCount < passage.wordMin
      ? t("summarizing.wordsToMinimum", { count: passage.wordMin - wordCount })
      : wordCount > passage.wordMax
        ? t("summarizing.wordsOverMaximum", { count: wordCount - passage.wordMax })
        : t("summarizing.withinRange");
  const metGoalCount = [inRange, copyingOkay].filter(Boolean).length;
  const advice = useMemo(() => {
    if (!passage || !adviceType) return null;

    const label = (en, ja) => locale === "ja" ? ja : en;
    const empty = wordCount === 0;
    const emptyMessage = label("Add your summary, then check its length and copying here.", "要約を書いてから、ここで語数とコピーを確認しましょう。");
    if (adviceType === "length") {
      const range = `${passage.wordMin}–${passage.wordMax}`;
      const message = label(`You wrote ${wordCount} words. Aim for ${range} words. `, `${wordCount} 語書きました。目標は ${range} 語です。`)
        + (inRange ? label("Good job. Your summary is within the target range.", "よくできました。目標範囲内です。")
          : wordCount < passage.wordMin ? label("Add the key details that are missing.", "足りない重要な情報を追加しましょう。")
            : label("Your summary is too long. Remove less important details.", "語数が多すぎます。重要でない情報を削りましょう。"));
      return {
        title: label("Check your length", "要約の語数を確認"),
        okay: inRange, empty,
        message: empty ? emptyMessage : message,
        chartTitle: label("Your result and target", "あなたの結果と目標"),
        bars: [
          { label: label("Minimum", "最小語数"), value: passage.wordMin, display: String(passage.wordMin), kind: "benchmark" },
          { label: label("Your length", "あなたの語数"), value: wordCount, display: String(wordCount), kind: "current" },
          { label: label("Maximum", "最大語数"), value: passage.wordMax, display: String(passage.wordMax), kind: "benchmark" }
        ],
        maxValue: Math.max(passage.wordMax, wordCount)
      };
    }
    if (adviceType === "copy") {
      const percent = copyingPercent * 100;
      const words = getWords(text);
      return {
        title: label("Check your copying", "コピーしすぎを確認"),
        okay: copyingOkay, empty,
        message: empty ? emptyMessage : copyingOkay
          ? label("Good job! Your summary is within the copying limit.", "よくできました！コピーは許容範囲内です。")
          : label("Rewrite the highlighted phrases in your own words. Keep the important meaning and change how you express it.", "ハイライトされた表現を自分の言葉で書き直しましょう。大切な意味を保ちながら、表現を変えましょう。"),
        chartTitle: label("Your result and target", "あなたの結果と目標"),
        bars: [
          { label: label("Copying limit", "コピー上限"), value: COPYING_LIMIT, display: "10%", kind: "benchmark" },
          { label: label("Your copying", "あなたのコピー"), value: copyingPercent, display: `${percent.toFixed(1)}%`, kind: "current" }
        ],
        maxValue: Math.max(COPYING_LIMIT, copyingPercent),
        feedback: !copyingOkay && words.length ? (
          <p className="writing-highlighted-text">
            {words.map((word, index) => (
              <span key={`${word}-${index}`} className={copying.copiedWords.has(index) ? "is-overused" : undefined}>{word}{" "}</span>
            ))}
          </p>
        ) : null
      };
    }

    return null;
  }, [adviceType, copying, copyingOkay, copyingPercent, inRange, passage, text, wordCount, locale]);

  return (
    <section className="writing-view summarizing-view" data-sielpa-practice>
      <div className="setup-panel compact-panel summary-setup-card">
        <label className="field-label" htmlFor="passage-select">{t("summarizing.currentPassage")}</label>
        <select id="passage-select" value={passageIndex} onChange={(event) => setPassageIndex(Number(event.target.value))}>
          {passages.map((_, index) => (
            <option key={index} value={index}>{t("summarizing.passage", { number: index + 1 })}</option>
          ))}
        </select>
      </div>

      <div className="directions-card full-span summary-brief">
        <div>
          <p className="kicker">{t("summarizing.kicker", { level: t(level.nameKey, {}, level.label) })}</p>
          <h2>{t("summarizing.prompt")}</h2>
          <p>{passage ? t("summarizing.writeRange", { range: rangeDetail }) : t("summarizing.loadingPassages")}</p>
        </div>
        <div className="summary-brief-stats" aria-label={t("summarizing.targetsLabel")}>
          <span>
            <strong>{passages.length || "-"}</strong>
            {t("common.passages")}
          </span>
          <span>
            <strong>{passageIndex + 1}</strong>
            {t("common.selected")}
          </span>
          <span>
            <strong>{passage ? `${passage.wordMin}-${passage.wordMax}` : "-"}</strong>
            {t("common.wordTarget")}
          </span>
          <span>
            <strong>{passage ? `${metGoalCount}/2` : "-"}</strong>
            {t("writing.goalsMet")}
          </span>
        </div>
      </div>

      <article className="source-card summary-source-card">
        <div className="summary-card-header">
          <div>
            <p className="kicker">{t("summarizing.sourceText")}</p>
            <h2>{t("summarizing.readingPassage")}</h2>
          </div>
          <span>{t("summarizing.passage", { number: passageIndex + 1 })}</span>
        </div>
        <div className="summary-source-body">
          <p>{passage?.passage || t("summarizing.noPassage")}</p>
        </div>
      </article>

      <div className="editor-card summary-editor-card">
        <div className="summary-card-header">
          <div>
            <p className="kicker">{t("summarizing.yourSummary")}</p>
            <h2>{t("summarizing.workspace")}</h2>
          </div>
          <span>{t("summarizing.rangeWords", { min: wordCount, max: wordCount }).replace(`${wordCount}-${wordCount}`, String(wordCount))}</span>
        </div>
        <textarea value={text} onChange={(event) => setText(event.target.value)} placeholder={t("writing.placeholder")} />
        <div className="summary-progress" aria-label={rangeStatus}>
          <span style={{ width: `${progress}%` }} />
        </div>
        <p className={inRange ? "summary-range is-good" : "summary-range"}>{rangeStatus}</p>
      </div>

      <aside className="goals-card summary-goals-card">
        <div className="summary-card-header">
          <div>
            <p className="kicker">{t("summarizing.qualityChecks")}</p>
            <h2>{t("summarizing.goals")}</h2>
          </div>
        </div>
        <div className="writing-advice-panel">
          <p>{t("summarizing.advicePrompt")}</p>
          <button type="button" className="writing-advice-button" onClick={() => setAdviceType("length")}>
            {t("writing.length")}
          </button>
          <button type="button" className="writing-advice-button" onClick={() => setAdviceType("copy")}>
            {t("summarizing.copying")}
          </button>
        </div>
        <MetricCard label={t("writing.length")} value={wordCount} detail={rangeDetail} status={inRange ? "good" : "needs-work"} />
        <MetricCard label={t("summarizing.target")} value={inRange ? t("summarizing.onTrack") : t("summarizing.revise")} detail={rangeStatus} status={inRange ? "good" : "needs-work"} />
        <MetricCard label={t("summarizing.copying")} value={`${Math.round(copyingPercent * 100)}%`} detail={t("summarizing.copyingLimit")} status={copyingOkay ? "good" : "needs-work"} />
        <PlusNudge className="workspace-plus-nudge" source="writing-adfree-hint">
          {t("writing.adfreeHint")}
        </PlusNudge>
      </aside>
      <SummaryAdviceModal advice={advice} type={adviceType} onSelect={setAdviceType} onClose={() => setAdviceType(null)} />
    </section>
  );
}
